import React, { useMemo } from 'react'
import { Header } from '../components/marketing/Header'
import { FeatureAnnouncementBanner } from '../components/marketing/FeatureAnnouncementBanner'
import Meta from '../components/Meta'
import Section from 'components/common/Section'
import { FeatureHero } from 'components/marketing/FeatureHero'
import { SectionHeader } from 'components/marketing/SectionHeader'
import CustomLink from 'designsystem/CustomLink'
import NextLink from 'next/link'
import NextImage from 'next/image'
import IconGrid, { IconGridItem } from 'components/marketing/IconGrid'
import IconRenderer from 'components/marketing/IconRenderer'
import { Button } from 'designsystem/Button'
import DemoVideo from 'components/DemoVideo'
import PodcastHighlight from 'components/marketing/PodcastHighlight'
import { getDepartments, getJobs } from 'utils/greenhouse'
import formatString from 'utils/formatString'
import JobDepartmentsList from 'components/marketing/JobDepartmentsList'
import JobsList from 'components/marketing/JobsList'
import { WinAsOneTeamIcon } from 'icons/WinAsOneTeam'
import { IntentionalityIcon } from 'icons/Intentionality'
import { MakeAnImpactIcon } from 'icons/MakeAnImpact'
import { IterateQuicklyIcon } from 'icons/IterateQuickly'

type CareersIndexProps = {
  jobs: GreenhouseJobOutput[]
  departments: GreenhouseEveryDepartment
}

export async function getStaticProps(): Promise<{
  props: CareersIndexProps
  revalidate: number
}> {
  return {
    props: {
      jobs: await getJobs(),
      departments: await getDepartments(),
    },
    revalidate: 10,
  }
}

const generalDepartment = 'dream-job'
const openApplication = 'dream-/-general-application'

export default function CareersIndex(props: CareersIndexProps): JSX.Element {
  const { jobs, departments } = props

  // Remove the "Dream Job" from the list
  const filteredJobs = useMemo(
    () => jobs.filter((job) => formatString(job.title) !== openApplication),
    [jobs],
  )

  // Remove the "No Department" and the "General Department" from the list
  const filteredDepartments = useMemo(
    () =>
      departments.filter((department) => {
        const currentDepartment = formatString(department.name)

        const isGeneralDepartment =
          currentDepartment === 'no-department' ||
          currentDepartment === generalDepartment

        const hasOpenJobs = department.jobs.length > 0

        return !isGeneralDepartment && hasOpenJobs
      }),
    [departments],
  )

  const dreamJob = useMemo(
    () =>
      departments
        .find(
          (department) => formatString(department.name) === generalDepartment,
        )
        ?.jobs.find((job) => formatString(job.title) === openApplication),
    [departments],
  )

  return (
    <div className='bg-grey'>
      <Meta
        title='Stellate is hiring!'
        description={`Stellate is building the future of the global data graph. Join us to unlock the world's data and create new opportunities for innovation.`}
        imageSrc='https://stellate.co/careers-social.png'
        imageWidth={1200}
        imageHeight={628}
      />
      <FeatureAnnouncementBanner />
      <Header />

      <main className='marketing-page'>
        <Section
          verticalPadding='first'
          className='flex flex-col sm:items-center'
        >
          <FeatureHero
            headline={
              <>
                Stellate Is Growing. <br /> Grow With Us.
              </>
            }
            subline={`Join us as we unlock the world's data.`}
            theme='blue'
          >
            {filteredJobs.length > 0 && (
              <CustomLink
                href='#jobs'
                text={`${filteredJobs.length} open ${
                  filteredJobs.length === 1 ? 'job' : 'jobs'
                }`}
                className='spacer-sm sm:mx-auto'
              />
            )}
          </FeatureHero>

          <div className='spacer-xl flex'>
            <NextImage
              src='/team/team-5.webp'
              className='rounded-12 shadow-big'
              alt='Stellate team'
              width='3000'
              height='1688'
              priority
            />
          </div>

          {filteredDepartments.length > 0 ? (
            <JobDepartmentsList
              className='spacer-sm'
              departments={filteredDepartments}
            />
          ) : null}

          {filteredDepartments.length === 0 && dreamJob ? (
            <div className='spacer-md flex'>
              <NextLink href='/handbook' target='_blank' rel='noreferrer'>
                <Button pageType='marketing' color='blue' tabIndex={-1}>
                  Apply for your Dream Job
                </Button>
              </NextLink>
            </div>
          ) : null}
        </Section>

        <Section noTopPadding>
          <SectionHeader label='Our vision' title='Who we are and what we do' />

          <DemoVideo
            url='https://www.youtube.com/watch?v=RTPn_IjudAM'
            thumbnail='/placeholders/careers_our-vision.webp'
          />

          <div className='spacer-sm grid gap-10 lg:grid-cols-2 lg:gap-12'>
            <h4>
              Our vision is to unlock the world&apos;s data by creating the
              global data graph. To achieve this vision, we start with making
              open GraphQL APIs ubiquitous.
            </h4>

            <p>
              By making the global data graph a reality, Stellate unlocks the
              compounding effects of innovation by{' '}
              <CustomLink
                hideArrow
                href='https://twitter.com/Austen/status/1520792630853918722'
                text={
                  'removing the inefficiencies of connecting poorly structured and documented APIs.'
                }
              />{' '}
              The recaptured development time alone is significant — less time
              spent on busy work allows for more time spent on innovation and
              value creation. Inevitably, this global data graph will be the
              center of the digital economy and Stellate will be the center of
              the global data graph.
            </p>
          </div>
        </Section>

        <Section bgColor='white'>
          <SectionHeader
            title='Our shared values'
            subline='We at Stellate are values-driven people. Shared values are the foundation of successful collaboration and enable us to do the best work of our lives.'
          />
          <ul
            className={`grid md:grid-cols-1 lg:grid-cols-2 gap-10 md:gap-x-32 md:gap-y-16`}
            {...props}
          >
            {workCulture.map((item, index) => (
              <li key={`${index}-${item.title}`}>
                <item.Icon className='lg:w-12 lg:h-12 w-8 h-8' />
                <h3 className='mt-3 h5-size'>{item.title}</h3>
                <p className='mt-3 opacity-60'>{item.description}</p>
              </li>
            ))}
          </ul>
        </Section>

        <Section>
          <SectionHeader
            title='The Stellate Future Founder Promise'
            subline={
              <>
                We are supportive of side-projects and want to enable anyone in
                the company to become a founder one day, if that is what you
                want to do.{' '}
                <CustomLink
                  hideArrow
                  href='https://www.youtube.com/watch?v=CAMENCQdX9I'
                  text={`That's afterall how Stellate started.`}
                />
              </>
            }
          />

          <PodcastHighlight />
        </Section>

        <Section bgColor='white'>
          <SectionHeader
            title='Supporting you to do your best work'
            subline='At Stellate we have one main principle: people-first. From this value flows everything we do, including, of course our perks.'
          />

          <IconGrid items={perks} />
        </Section>

        <Section
          noTopPadding
          bgColor='white'
          className='flex flex-col items-start sm:items-center'
        >
          <IconRenderer icon='diamond' className='h-20 w-auto text-blue' />

          <SectionHeader
            title='Want to know how we work?'
            subline='Check out our open employee handbook!'
            className='spacer-sm'
            paddingToContent='small'
          />

          <NextLink href='/handbook'>
            <Button pageType='marketing' color='blue' tabIndex={-1}>
              Open Employee Handbook
            </Button>
          </NextLink>
        </Section>

        <Section noTopPadding id='jobs'>
          <JobsList jobs={jobs} departments={filteredDepartments} />
        </Section>

        {dreamJob && (
          <Section
            noTopPadding
            className='flex flex-col items-start sm:items-center'
            id='dream-job'
          >
            <IconRenderer
              icon='shooting-star'
              className='h-20 w-auto text-blue'
            />

            <SectionHeader
              title='Dream job'
              subline='What job do you wish you could have?'
              className='spacer-sm'
              paddingToContent='small'
            />

            <a href={dreamJob.absolute_url} target='_blank' rel='noreferrer'>
              <Button pageType='marketing' color='blue' tabIndex={-1}>
                Apply for your dream job
              </Button>
            </a>
          </Section>
        )}
      </main>
    </div>
  )
}

const workCulture = [
  {
    title: 'Win as one team',
    Icon: WinAsOneTeamIcon,
    description:
      "We can't win alone, we have to win together. Combining our unique experiences and skillsets allows us to achieve more together. By fostering an environment of trust, collaboration, and growth we can maximize our collective potential and do the best work of our lives.",
  },
  {
    title: 'Intentionality',
    Icon: IntentionalityIcon,
    description: `Intentionality permeates everything we do. Our actions demonstrate our choices in the "how" and "why" of the way we show up to the broader ecosystem and to each other. Our actions build the foundation of trust, vulnerability, and accountability.`,
  },
  {
    title: 'Make an impact',
    Icon: MakeAnImpactIcon,
    description: `The story of a startup has two ends: Change the world or die. To change the world, we need to build quality products that solve real problems in a way that is better, greater, and quicker than everything that came before. We can succeed in positively impacting people's lives by growing, learning, and elevating those around us.`,
  },
  {
    title: 'Iterate quickly',
    Icon: IterateQuicklyIcon,
    description: `A startup is a race against running out of funds and market competition. The only way to win this race is to run fast in the right direction (velocity). We win if we discover ways to solve problems engineering organizations face at a speed greater than our competition and before the funds run out. We do this by testing our assumptions and quickly iterating upon our learnings.`,
  },
]

const perks: IconGridItem[] = [
  {
    title: 'Work Environment & Equipment',
    icon: 'equipment',
    description: `You need it, you get it. As a part of the team, we simply trust you not to overspend.`,
  },
  {
    title: 'Flexible time off',
    icon: 'time-off',
    description: `If we hire you, it means we trust you. Take time off if you need it. We don't micromanage input and only assess output.`,
  },
  {
    title: 'Coaching for all',
    icon: 'coaching',
    description: `No matter how good you are, there is always room for growth. That's why we offer professional coaching to all team members.`,
  },
  {
    title: 'Industry Conferences & Conventions',
    icon: 'conferences',
    description: `Network, learn and grow at the leading conferences around the world. It's a mutual benefit.`,
  },
  {
    title: 'Connect with your Colleagues',
    icon: 'travel',
    description: `Socializing on Zoom is tough. Visit colleagues up to three times a year, no matter where.`,
  },
  {
    title: 'Employee Development',
    icon: 'development',
    description: `Aside from the coaching, we support your ambitions to learn and grow with up to $2,5k a year.`,
  },
  {
    title: 'Health benefits',
    icon: 'health-benefits',
    description: `Your health is our concern and responsibility. Full insurance coverage is obligatory and your mental health is taken seriously.`,
  },
  {
    title: 'Retirement Plans',
    icon: 'retirement-plan',
    description: `It's hard to imagine retiring one day, right? Keep your focus on the present with full support for your retirement plan.`,
  },
  {
    title: 'Referral Fees',
    icon: 'referral-fees',
    description: `Know some talent or a potential client? Of course, you do! Get a fixed $3k referral fee for every successfull recommendation.`,
  },
  {
    title: 'Meal Perks',
    icon: 'meal',
    description: `Break bread with the team. Every meal shared with at least one colleague or when on PagerDuty is paid for by the company.`,
  },
  {
    title: 'Your favourite Perk',
    icon: 'plus',
    description: `What do you think is important for a happy and productive work place? We're happy to hear your proposals for the next perk.`,
  },
]
